import { useCallback, useEffect, useState } from 'react'

import DiscoverListingCard from '../discover-listing-card/DiscoverListingCard'
import { useAuth } from '../../_hooks/use-auth'
import { useQuickPassProfile } from '../../_hooks/use-quickpass-profile'
import { fetchDiscoverListingsByIds } from '../../_data/discover'
import { RecentlyViewedDiscoverState } from './types'
import {
  ScrollRail,
  SectionHeader,
  SectionLink,
  SectionRoot,
  SectionTitle,
  SkeletonRow,
} from './styled'

const VISIBLE_LIMIT = 6

/**
 * Dashboard rail surfacing the renter's most recently viewed discover
 * listings. Hidden entirely when the renter hasn't viewed anything yet —
 * dashboards live or die on signal-to-noise, and a placeholder rail with
 * "you haven't viewed anything yet" is pure noise.
 *
 * @returns {JSX.Element | null}
 */
const RecentlyViewedDiscover = (): JSX.Element | null => {
  const { quickPassUser } = useAuth()
  const { profile, saveListingToProfile, unsaveListingFromProfile } =
    useQuickPassProfile()
  const [state, setState] = useState<RecentlyViewedDiscoverState>({
    listings: [],
    isLoading: true,
  })

  useEffect(() => {
    if (quickPassUser === null || profile === null) {
      setState({ listings: [], isLoading: false })
      return
    }

    const ids = (profile.recentlyViewedDiscoverIds ?? []).slice(
      0,
      VISIBLE_LIMIT,
    )
    if (ids.length === 0) {
      setState({ listings: [], isLoading: false })
      return
    }

    let isMounted = true
    setState(previous => ({ ...previous, isLoading: true }))

    void fetchDiscoverListingsByIds({
      ids,
      quickPassUserId: quickPassUser.id,
    }).then(listings => {
      if (isMounted === false) return
      setState({ listings, isLoading: false })
    })

    return () => {
      isMounted = false
    }
  }, [profile, quickPassUser])

  const handleSaveToggle = useCallback(
    async (listingId: string): Promise<void> => {
      if (profile === null) return
      try {
        if (profile.savedListingIds.includes(listingId) === true) {
          await unsaveListingFromProfile(listingId)
        } else {
          await saveListingToProfile(listingId)
        }
      } catch (error) {
        console.error('[recently-viewed] save toggle failed', error)
      }
    },
    [profile, saveListingToProfile, unsaveListingFromProfile],
  )

  if (profile === null) {
    return null
  }

  if (state.isLoading === false && state.listings.length === 0) {
    return null
  }

  const savedSet = new Set<string>(profile.savedListingIds)

  return (
    <SectionRoot>
      <SectionHeader>
        <SectionTitle>Recently viewed</SectionTitle>
        <SectionLink to="/discover">Discover more</SectionLink>
      </SectionHeader>

      {state.isLoading === true ? (
        <ScrollRail>
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
        </ScrollRail>
      ) : (
        <ScrollRail>
          {state.listings.map(listing => (
            <DiscoverListingCard
              key={listing.id}
              listing={listing}
              isSaved={savedSet.has(listing.id)}
              onSaveToggle={handleSaveToggle}
            />
          ))}
        </ScrollRail>
      )}
    </SectionRoot>
  )
}

export default RecentlyViewedDiscover
